import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Package as PackageIcon, ShoppingBag, Mail, Plus, Pencil, Trash2 } from "lucide-react";
import { adminApi, clearToken, getToken } from "../admin/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const STATUSES = ["pending", "paid", "confirmed", "cancelled", "refunded"];
const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  confirmed: "bg-sky-100 text-sky-800",
  cancelled: "bg-slate-200 text-slate-700",
  refunded: "bg-rose-100 text-rose-800",
};

const EMPTY_PKG = {
  slug: "", name: "", destination: "", nights: 5, price_from: 1000, deposit: 500,
  tag: "", image: "", alt: "", highlights: "", active: true,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [packages, setPackages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [pkgDialog, setPkgDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pkgForm, setPkgForm] = useState(EMPTY_PKG);
  const [saving, setSaving] = useState(false);

  const logout = useCallback(() => {
    clearToken();
    navigate("/admin/login");
  }, [navigate]);

  const loadAll = useCallback(async () => {
    try {
      const api = adminApi();
      const [o, p, l] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/packages"),
        api.get("/admin/inquiries"),
      ]);
      setOrders(o.data);
      setPackages(p.data);
      setLeads(l.data);
    } catch (err) {
      if (err.response?.status === 401) logout();
    }
  }, [logout]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    adminApi()
      .get("/auth/me")
      .then(() => {
        setReady(true);
        loadAll();
      })
      .catch(() => logout());
  }, [navigate, logout, loadAll]);

  const changeStatus = async (order, status) => {
    try {
      await adminApi().patch(`/admin/orders/${order.id}`, { status });
      toast.success(`Order marked ${status}`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const openNewPkg = () => {
    setEditing(null);
    setPkgForm(EMPTY_PKG);
    setPkgDialog(true);
  };
  const openEditPkg = (p) => {
    setEditing(p);
    setPkgForm({ ...p, highlights: (p.highlights || []).join(", ") });
    setPkgDialog(true);
  };

  const savePkg = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      slug: pkgForm.slug,
      name: pkgForm.name,
      destination: pkgForm.destination,
      nights: Number(pkgForm.nights),
      price_from: Number(pkgForm.price_from),
      deposit: Number(pkgForm.deposit),
      tag: pkgForm.tag || null,
      image: pkgForm.image,
      alt: pkgForm.alt || null,
      highlights: String(pkgForm.highlights)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      active: pkgForm.active !== false,
    };
    try {
      if (editing) {
        await adminApi().put(`/admin/packages/${editing.id}`, payload);
        toast.success("Package updated");
      } else {
        await adminApi().post("/admin/packages", payload);
        toast.success("Package created");
      }
      setPkgDialog(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deletePkg = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await adminApi().delete(`/admin/packages/${p.id}`);
      toast.success("Package deleted");
      loadAll();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const upd = (k) => (e) => setPkgForm((f) => ({ ...f, [k]: e.target.value }));

  const tabs = [
    { key: "orders", label: "Orders", icon: ShoppingBag, count: orders.length },
    { key: "packages", label: "Packages", icon: PackageIcon, count: packages.length },
    { key: "leads", label: "Leads", icon: Mail, count: leads.length },
  ];

  return (
    <div className="min-h-screen bg-muted pt-24" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold text-navy">Back Office</h1>
            <p className="text-slate-500">Manage bookings, packages, and leads.</p>
          </div>
          <Button
            data-testid="admin-logout-button"
            onClick={logout}
            variant="outline"
            className="rounded-full"
          >
            <LogOut className="mr-2 h-4 w-4" /> Log out
          </Button>
        </div>

        {/* Stat cards */}
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <StatCard label="Total revenue (paid)" value={money(orders.filter((o) => ["paid", "confirmed"].includes(o.status)).reduce((s, o) => s + o.amount, 0))} />
          <StatCard label="Paid bookings" value={orders.filter((o) => ["paid", "confirmed"].includes(o.status)).length} />
          <StatCard label="Pending" value={orders.filter((o) => o.status === "pending").length} />
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-2 border-b border-slate-200">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                tab === t.key
                  ? "border-primary text-primary"
                  : "border-transparent text-slate-500 hover:text-navy"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              <span className="rounded-full bg-slate-100 px-2 text-xs text-slate-600">{t.count}</span>
            </button>
          ))}
        </div>

        {/* Orders */}
        {tab === "orders" && (
          <div className="mt-6 overflow-x-auto rounded-2xl bg-white shadow-sm" data-testid="admin-orders-table">
            <table className="w-full text-left text-sm">
              <thead className="bg-navy text-white">
                <tr>
                  {["Package", "Customer", "Travelers", "Paid", "Balance", "Type", "Status", "Action"].map((h) => (
                    <th key={h} className="px-4 py-3 font-display font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">No orders yet.</td></tr>
                )}
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-semibold text-navy">{o.package_name}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {o.customer?.name}<br />
                      <span className="text-xs text-slate-400">{o.customer?.email}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{o.travelers}</td>
                    <td className="px-4 py-3 text-slate-600">{money(o.amount)}</td>
                    <td className="px-4 py-3 text-slate-600">{money(o.balance_due)}</td>
                    <td className="px-4 py-3 text-slate-600 capitalize">{o.payment_type}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_COLOR[o.status] || ""}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={o.status} onValueChange={(v) => changeStatus(o, v)}>
                        <SelectTrigger className="h-9 w-36 rounded-lg" data-testid={`order-status-${o.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Packages */}
        {tab === "packages" && (
          <div className="mt-6">
            <div className="mb-4 flex justify-end">
              <Button data-testid="admin-add-package" onClick={openNewPkg} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-2 h-4 w-4" /> Add package
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {packages.map((p) => (
                <div key={p.id} className="overflow-hidden rounded-2xl bg-white shadow-sm" data-testid={`admin-package-${p.slug}`}>
                  <img src={p.image} alt={p.alt} className="h-36 w-full object-cover" />
                  <div className="p-4">
                    <h3 className="font-display font-bold text-navy">{p.name}</h3>
                    <p className="text-xs text-slate-500">{p.destination}</p>
                    <p className="mt-2 text-sm text-navy">{money(p.price_from)}/pp · {money(p.deposit)} deposit</p>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEditPkg(p)} data-testid={`edit-package-${p.slug}`}>
                        <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full text-rose-600" onClick={() => deletePkg(p)} data-testid={`delete-package-${p.slug}`}>
                        <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Leads */}
        {tab === "leads" && (
          <div className="mt-6 overflow-x-auto rounded-2xl bg-white shadow-sm" data-testid="admin-leads-table">
            <table className="w-full text-left text-sm">
              <thead className="bg-navy text-white">
                <tr>
                  {["Name", "Email", "Phone", "Package", "Message", "Date"].map((h) => (
                    <th key={h} className="px-4 py-3 font-display font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-400">No leads yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-semibold text-navy">{l.name}</td>
                    <td className="px-4 py-3 text-slate-600">{l.email}</td>
                    <td className="px-4 py-3 text-slate-600">{l.phone || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{l.package_name || "Custom"}</td>
                    <td className="px-4 py-3 text-slate-500 max-w-xs truncate">{l.message || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{(l.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Package form dialog */}
      <Dialog open={pkgDialog} onOpenChange={setPkgDialog}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-3xl" data-testid="package-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-navy">
              {editing ? "Edit package" : "New package"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={savePkg} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Name"><Input value={pkgForm.name} onChange={upd("name")} required className="rounded-xl" data-testid="pkg-name" /></FormField>
              <FormField label="Slug (unique)"><Input value={pkgForm.slug} onChange={upd("slug")} required disabled={!!editing} className="rounded-xl" data-testid="pkg-slug" /></FormField>
            </div>
            <FormField label="Destination"><Input value={pkgForm.destination} onChange={upd("destination")} required className="rounded-xl" data-testid="pkg-destination" /></FormField>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Nights"><Input type="number" value={pkgForm.nights} onChange={upd("nights")} className="rounded-xl" data-testid="pkg-nights" /></FormField>
              <FormField label="Price /pp"><Input type="number" value={pkgForm.price_from} onChange={upd("price_from")} className="rounded-xl" data-testid="pkg-price" /></FormField>
              <FormField label="Deposit"><Input type="number" value={pkgForm.deposit} onChange={upd("deposit")} className="rounded-xl" data-testid="pkg-deposit" /></FormField>
            </div>
            <FormField label="Tag"><Input value={pkgForm.tag || ""} onChange={upd("tag")} placeholder="Bestseller" className="rounded-xl" data-testid="pkg-tag" /></FormField>
            <FormField label="Image URL"><Input value={pkgForm.image} onChange={upd("image")} required className="rounded-xl" data-testid="pkg-image" /></FormField>
            <FormField label="Image alt text"><Input value={pkgForm.alt || ""} onChange={upd("alt")} className="rounded-xl" data-testid="pkg-alt" /></FormField>
            <FormField label="Highlights (comma separated)"><Input value={pkgForm.highlights} onChange={upd("highlights")} placeholder="Villa, All-inclusive, Cruise" className="rounded-xl" data-testid="pkg-highlights" /></FormField>
            <Button type="submit" disabled={saving} className="w-full rounded-full bg-primary py-6 font-semibold text-primary-foreground hover:bg-primary/90" data-testid="pkg-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Create package"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ label, value }) => (
  <div className="rounded-2xl bg-white p-6 shadow-sm">
    <p className="text-sm text-slate-500">{label}</p>
    <p className="mt-1 font-display text-2xl font-bold text-navy">{value}</p>
  </div>
);

const FormField = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-navy">{label}</Label>
    {children}
  </div>
);
